import { useState } from "react";
import { api, type GrowthAction, type GrowthCategory, type GrowthDifficulty, type ProductRow } from "../api";
import { useAsync } from "../useAsync";
import { money } from "../format";

const CATEGORY: Record<GrowthCategory, string> = {
  activation: "Activation",
  friction: "Friction",
  acquisition: "Acquisition",
  pricing: "Pricing",
  seo: "SEO",
};

const DIFFICULTY: Record<GrowthDifficulty, string> = {
  quick_fix: "Quick fix",
  feature_build: "Feature build",
  experiment: "Experiment",
};

/**
 * The next three things to build. Sits above the dashboard widgets so the
 * first screen is "do this", not another chart.
 */
export function GrowthActionsBar({ product }: { product: ProductRow }) {
  const report = useAsync(
    () => api.growthActions(product.slug, 30, "cursor"),
    [product.slug],
    { key: `growth:${product.slug}` },
  );
  const actions = (report.data?.actions ?? []).slice(0, 3);

  if (report.error) return null;
  if (report.loading && !report.data) return null;
  if (actions.length === 0) return null;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
        <div className="eyebrow">Next growth steps</div>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          Ranked by estimated monthly impact. Copy a prompt into Cursor or Claude.
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {actions.map((a) => (
          <GrowthRow key={a.id} action={a} slug={product.slug} />
        ))}
      </div>
    </div>
  );
}

function GrowthRow({ action, slug }: { action: GrowthAction; slug: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(action.prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      const fresh = await api.growthPrompt(slug, action.id, "cursor");
      await navigator.clipboard.writeText(fresh.prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  const impact = action.revenue_impact_cents !== null
    ? `Est. ${money(action.revenue_impact_cents, action.currency)}/mo`
    : null;

  return (
    <div className="panel" style={{ padding: "14px 16px 12px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
            <span className="eyebrow" style={{ color: "var(--keeper)" }}>{CATEGORY[action.category]}</span>
            <span className="eyebrow">{DIFFICULTY[action.difficulty]}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 550, letterSpacing: "-0.02em", lineHeight: 1.35 }}>
            {action.headline}
          </div>
          <div className="num" style={{ marginTop: 6, fontSize: 13, color: impact ? "var(--pos)" : "var(--text-3)" }}>
            {impact ?? action.metric_line}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" className="btn-accent" onClick={copy}>
            {copied ? "Copied" : "Copy prompt"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? "Hide" : "Why"}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-2)", lineHeight: 1.55, maxWidth: 72 * 8 }}>
          <p style={{ margin: "0 0 8px" }}>{action.evidence}</p>
          <p style={{ margin: 0 }}>{action.instruction}</p>
        </div>
      )}
    </div>
  );
}
